"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VERSIONED_DEAL_FIELDS = exports.DealVersionSchema = exports.CommissionPlanSnapshotSchema = exports.PinnedDocumentSchema = void 0;
exports.activeVersion = activeVersion;
exports.nextVersionNo = nextVersionNo;
exports.isDealVersionStale = isDealVersionStale;
exports.applyDealVersion = applyDealVersion;
const zod_1 = require("zod");
const planAssignment_1 = require("./planAssignment");
const dealDevice_1 = require("./dealDevice");
/**
 * A PINNED document reference. `versionNo` is REQUIRED — the entire point of
 * this schema is that a deal cannot say "latest". Two customers who signed the
 * same deal weeks apart must be able to be shown exactly what each agreed to.
 */
exports.PinnedDocumentSchema = zod_1.z.object({
    documentId: zod_1.z.string().min(1),
    versionNo: zod_1.z.number().int().positive(),
    /**
     * `policy` is the third section (2026-09-01). A financing policy is a bound
     * document like any other (`documentBindings.ts` binding kind
     * `financing_policy`) and had no section to be pinned under, so the one class
     * of paperwork that states early-payoff and default terms — the terms a
     * merchant is most likely to dispute — was the one class a deal version could
     * not freeze. Appended, never reordered: `section` is persisted on published
     * rows and its values are evidence.
     */
    section: zod_1.z.enum(['software', 'hardware', 'policy']),
    required: zod_1.z.boolean(),
});
/**
 * A SNAPSHOT of the plan's terms, never just its id. Storing `commissionPlanId`
 * would reintroduce pull-latest on the one dimension where money is owed to a
 * third party: editing a plan would retroactively change what a signed deal
 * promised a partner.
 */
exports.CommissionPlanSnapshotSchema = zod_1.z.object({
    planId: zod_1.z.string().min(1),
    name: zod_1.z.string().min(1),
    softwareRatePercent: zod_1.z.number().nonnegative().optional(),
    hardwareFlatCents: zod_1.z.number().int().nonnegative().optional(),
    perDeviceFlatCents: zod_1.z.record(zod_1.z.string(), zod_1.z.number().int().nonnegative()).optional(),
});
exports.DealVersionSchema = zod_1.z.object({
    dealId: zod_1.z.string().min(1),
    versionNo: zod_1.z.number().int().positive(),
    status: zod_1.z.enum(['draft', 'published', 'retired']),
    /** No default, deliberately: the publisher chooses, as the document
     *  PublishModal already requires. Material flags older signed versions stale. */
    changeType: zod_1.z.enum(['material', 'minor']),
    effectiveAt: zod_1.z.string().min(1),
    publishedBy: zod_1.z.string().min(1),
    publishedAt: zod_1.z.string().min(1),
    /**
     * THE MONEY. The deal's effective monthly software price, in cents, at the
     * moment this version was frozen.
     *
     * Optional on the SCHEMA so a draft written before this field still parses —
     * but `publishDraft` REFUSES to publish without it. A version that freezes
     * the packages, the documents and the commission while leaving the price to
     * be read from the live deal row is not a frozen contract at all: change the
     * deal's price afterwards and every "signed" merchant silently re-prices.
     */
    priceCents: zod_1.z.number().int().nonnegative().optional(),
    packageIds: zod_1.z.array(zod_1.z.string()).default([]),
    packageDiscounts: zod_1.z.record(zod_1.z.string(), planAssignment_1.DiscountSchema).default({}),
    pricePhases: zod_1.z.array(zod_1.z.any()).default([]),
    devicePackageIds: zod_1.z.array(zod_1.z.string()).default([]),
    deviceTerms: zod_1.z.record(zod_1.z.string(), dealDevice_1.DealDeviceTermSchema).default({}),
    freeLocationQuota: zod_1.z.number().int().nonnegative(),
    additionalLocationPriceCents: zod_1.z.number().int().nonnegative(),
    documents: zod_1.z.array(exports.PinnedDocumentSchema).default([]),
    commissionPlan: exports.CommissionPlanSnapshotSchema,
    /** Operator-supplied constants satisfying the publish gate (spec §8). Frozen
     *  with the version, so an override can never change under a signed deal. */
    mergeOverrides: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).default({}),
});
/**
 * DERIVED, never stored (spec V4). Active = the published version with the
 * greatest effectiveAt that has already arrived. Same rule the document engine
 * uses, and it is what removes a cron and a batch sweep.
 */
function activeVersion(versions, now = new Date()) {
    const nowMs = now.getTime();
    return versions
        .filter(v => v.status === 'published' && Date.parse(v.effectiveAt) <= nowMs)
        .sort((a, b) => Date.parse(b.effectiveAt) - Date.parse(a.effectiveAt)
        || b.versionNo - a.versionNo)[0];
}
/** One past the highest number EVER used — a retired version's number is never
 *  reissued, or two different contracts would share an identifier. */
function nextVersionNo(versions) {
    return versions.reduce((max, v) => Math.max(max, v.versionNo), 0) + 1;
}
/**
 * DERIVED ON READ (spec V4). A material publish on a pinned document does not
 * silently change the deal — it makes the deal STALE, and an operator publishes
 * a new deal version to adopt it. A minor publish changes nothing, by
 * definition. An UNKNOWN document (retired, deleted) is reported with a null
 * active version rather than dropped: a deal pinned to a document nobody can
 * find is exactly the case an operator must see.
 */
function isDealVersionStale(version, documentIndex) {
    const out = [];
    for (const pinned of version.documents) {
        const entry = documentIndex[pinned.documentId];
        if (!entry) {
            out.push({ documentId: pinned.documentId, pinnedVersionNo: pinned.versionNo, activeVersionNo: null });
            continue;
        }
        if (entry.materialVersionsAfter(pinned.versionNo).length > 0) {
            out.push({ documentId: pinned.documentId, pinnedVersionNo: pinned.versionNo, activeVersionNo: entry.activeVersionNo });
        }
    }
    return out;
}
/**
 * THE FIELDS A VERSION FREEZES — the ones that decide money or what is signed.
 *
 * Named as data rather than spelled out in `applyDealVersion`'s body so the
 * publish path, the read path and the super-admin editor can all agree on one
 * list. A field that is not here is OPERATIONAL: `isActive`,
 * `showOnLandingPage`, `maxUses`, `expiresAt`, the landing-page copy, the
 * affiliate attribution. Those keep saving instantly and are read live —
 * pausing a deal that is mis-selling must never wait on a publish.
 *
 * ⚠️ `documents` IS DELIBERATELY ABSENT, though a version freezes it.
 * The name means two different things: on a DEAL ROW it is the binder config
 * (`{ hardware: { agreementDocumentIds: [...] } }`, an object), on a VERSION it
 * is the pinned `documentId@versionNo` LIST (an array). Overlaying one onto the
 * other makes the deal row fail `DealSchema` — "documents Expected object,
 * received array" — and a tenant whose deal cannot be parsed is quoted no deal
 * at all, silently, at list price. The pinned list is read straight off the
 * version by the signing path, which is the only thing that wants it.
 */
exports.VERSIONED_DEAL_FIELDS = [
    'priceCents',
    'monthlyAmount',
    'packageIds',
    'packageDiscounts',
    'packageIntros',
    'pricePhases',
    'devicePackageIds',
    'deviceSkuIds',
    'deviceTerms',
    'freeLocationQuota',
    'additionalLocationPriceCents',
    'commissionPlanId',
    'commissionMode',
    'flatFee',
    'percentageRate',
    'trialDays',
    'offerTerms',
    'mergeOverrides',
];
/**
 * THE DEAL AS ONE VERSION FROZE IT — the live row's identity, the version's money.
 *
 * PIN, DON'T PULL. A customer keeps the price, packages, hardware, commission
 * and paperwork of the version they signed, forever. Every one of those lived
 * on the live deal row and was therefore read as "latest", so editing a deal
 * silently re-priced merchants who had already signed it and changed what the
 * next one would sign with no record of the difference. This is the function
 * that stops that: the row supplies WHO the deal is, the version supplies WHAT
 * it costs.
 *
 * `devicePackageIds` and `deviceSkuIds` are the same concept under two names
 * (see `deal.ts`) and are written in LOCKSTEP here, from whichever the version
 * carries — overlaying one and leaving the other live is how the hardware step
 * and the invoice come to disagree about which devices an offer includes.
 *
 * A `null`/`undefined` version returns the row UNCHANGED, which is an
 * unversioned legacy deal read as an implicit v1. That is what makes this
 * rollout need no migration.
 *
 * Pure. No I/O, no clock.
 */
function applyDealVersion(row, version) {
    if (!version)
        return row;
    const merged = { ...row };
    for (const field of exports.VERSIONED_DEAL_FIELDS) {
        // `undefined` means the version does not carry the field, so the row's own
        // value stands. Deliberately NOT `in`-based: a draft written before a field
        // existed must not blank that field on every deal it is applied to.
        if (version[field] !== undefined)
            merged[field] = version[field];
    }
    // The two spellings of "the hardware this offer includes", kept identical.
    const devices = version.devicePackageIds ?? version.deviceSkuIds;
    if (devices !== undefined) {
        merged.devicePackageIds = devices;
        merged.deviceSkuIds = devices;
    }
    // `monthlyAmount` is the deal row's name for what a version calls
    // `priceCents`. A version that froze the price sets both, so a reader that
    // knows only one of the two names cannot read a stale figure.
    if (version.priceCents !== undefined && version.monthlyAmount === undefined) {
        merged.monthlyAmount = version.priceCents;
    }
    return merged;
}
//# sourceMappingURL=dealVersion.js.map